import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorState, ExportButton, FiltersSection, LoadingState, MismatchesAgGridTable } from '../';
import { useAgGridColumns, useMismatches, useMismatchesMain } from '../../hooks';

export const Main = () => {
  const { t } = useTranslation('codMismatches');
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    filters,
    updateFilters,
    resetFilters,
    handleSortingChange,
    sorting,
    searchTerm,
    handleSearch,
    hasActiveFilters,
  } = useMismatchesMain();

  const { mismatches, total, isLoading, isFetching, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useMismatches({
      filters,
    });

  const { columnDefs } = useAgGridColumns();

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  // Configurar breadcrumbs para la topbar
  const breadcrumbs = useMemo(() => [{ label: t('breadcrumb.mismatches', 'Descuadres') }], [t]);

  // Establecer breadcrumbs en la topbar
  usePageNavigation({
    breadcrumbs,
  });

  // Props para la vista
  const mismatchesViewProps = useMemo(
    () => ({
      mismatches,
      isLoading: isInitialLoad && isLoading,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      total,
    }),
    [mismatches, isInitialLoad, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, total],
  );

  // 🎯 Calcular si es un fetch en background (no inicial, no scroll infinito)
  const isBackgroundFetching = !isInitialLoad && isFetching && !isFetchingNextPage;

  // Estado de error
  if (error) {
    return (
      <div className="px-6 pt-4">
        <ErrorState message={error} onRetry={refetch} />
      </div>
    );
  }

  // 🎯 Muestra el spinner SOLO en la carga inicial
  if (isInitialLoad) {
    return (
      <div className="px-6 pt-4">
        <LoadingState message={t('loading.mismatches', 'Cargando descuadres...')} />
      </div>
    );
  }

  return (
    <div className="px-6 pt-4">
      {/* Sección de filtros y total */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <FiltersSection
            filters={filters}
            onFiltersChange={updateFilters}
            onResetFilters={resetFilters}
            searchTerm={searchTerm}
            onSearch={handleSearch}
          />
          <ExportButton filters={filters} />
        </div>
        <TotalResults total={total} />
      </div>

      {/* Tabla - solo se renderiza cuando hay datos */}
      {mismatches.length > 0 && (
        <div
          className={`transition-opacity duration-300 ${
            isBackgroundFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
        >
          <MismatchesAgGridTable
            mismatches={mismatchesViewProps.mismatches}
            columnDefs={columnDefs}
            isLoading={mismatchesViewProps.isLoading}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            fetchNextPage={mismatchesViewProps.fetchNextPage}
            hasNextPage={mismatchesViewProps.hasNextPage}
            isFetchingNextPage={mismatchesViewProps.isFetchingNextPage}
          />
        </div>
      )}

      {/* Estado vacío */}
      {mismatches.length === 0 && (
        <EmptyView
          type={hasActiveFilters ? 'noResults' : 'emptyState'}
          icon={EMPTY_VIEW_ICONS.DEFAULT}
          title={
            hasActiveFilters
              ? t('empty.no_results_title', 'No se encontraron resultados')
              : t('empty.title', 'No hay descuadres')
          }
          description={
            hasActiveFilters
              ? t(
                  'empty.no_results_description',
                  'No hay descuadres que coincidan con los filtros aplicados. Intenta ajustar los filtros.',
                )
              : t('empty.description', 'No se encontraron descuadres en el ledger')
          }
        />
      )}
    </div>
  );
};
